import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { io } from 'socket.io-client';
import { loginSuccess } from '../../slices/authSlice';
import { storeAuthToken } from '../../utils/authToken';

// Landing page for the SAML SSO redirect. The backend ACS validates the
// assertion, then bounces the browser here with a freshly minted auth token in
// the query string. We exchange it for the user profile and complete the same
// session bootstrap the password flow uses. If 2FA is enrolled, the backend
// sends the user to /two-factor/challenge instead and never reaches this page.
const RushSsoCallbackPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  // StrictMode double-invokes effects in dev; guard so we don't complete twice.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = params.get('token');
    const errCode = params.get('error');
    if (errCode || !token) {
      navigate(`/login?error=${encodeURIComponent(errCode || 'sso_failed')}`, { replace: true });
      return;
    }

    (async () => {
      try {
        // SSO sessions are long-lived (the IdP is the source of truth), so
        // persist the token like "remember me".
        storeAuthToken(token, true);
        const me = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = me.data;
        dispatch(loginSuccess({ token, user }));
        try {
          const socket = io('/', { transports: ['websocket', 'polling'] });
          socket.emit('join', user._id);
          window.__SOCKET__ = socket;
        } catch (_) {
          // Socket failure shouldn't block navigation.
        }
        navigate('/dashboard', { replace: true });
      } catch (_) {
        setError('We could not complete your single sign-on. Please try again.');
      }
    })();
  }, [params, navigate, dispatch]);

  return (
    <div className="rs auth-page" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        {error ? (
          <>
            <h1 className="auth-title">Sign-in failed</h1>
            <p className="auth-sub">{error}</p>
            <button className="btn btn-accent" onClick={() => navigate('/login', { replace: true })}>
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 className="auth-title">Signing you in…</h1>
            <p className="auth-sub">Completing single sign-on with your organisation.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default RushSsoCallbackPage;
